<script setup lang="ts">
/** Revisions: View (non-destructive) and Restore (an undoable edit). */
import { computed } from 'vue';
import { describeChange } from '@/domain';
import Icon from './Icon.vue';
import AppDialog from './AppDialog.vue';
import { app, snap, ui } from '@/app/state';
import { announce, toast } from '@/app/announce';
import { changeLabel, nextRevId, viewRevision } from '@/app/navigation';
import { fmtDate, short } from '@/app/format';
import { noteRegenerated } from '@/app/persistence';

const s = computed(() => snap.value);
const revs = computed(() => s.value.revisions.slice().reverse().map((r) => {
  const i = s.value.revisions.indexOf(r);
  const prev = s.value.revisions[i - 1];
  const changes = prev ? describeChange(prev.design, r.design, s.value.stock, s.value.construction) : [r.kind === 'manual' ? 'Set up by hand' : 'First proposal from the reference'];
  return {
    r, changes: changes.slice(0, 3).join(' · ') || (r.kind === 'layout' ? 'Layout only' : 'Appearance only'),
    cur: r.id === s.value.currentRevisionId,
    exported: s.value.exports.some((e) => e.revisionId === r.id),
  };
}));
const nowChange = computed(() => (s.value.currentRevision ? describeChange(s.value.currentRevision.design, s.value.design, s.value.stock, s.value.construction)[0] : null) ?? 'Appearance or layout changed');

function close() { ui.historyOpen = false; }
function view(id: string) { close(); viewRevision(id); }
function restore(id: string) {
  const r = app.store.revision(id);
  if (!r) return;
  close();
  app.store.restoreRevision(id, { label: `Restored ${id}` });
  toast(`Restored the design of ${id}. Undo to go back.`);
  announce(`Restored ${id}.`);
  noteRegenerated(id);
}
function commit() {
  const r = app.store.commitRevision(changeLabel('Saved by you'));
  close();
  toast(`Saved as ${r.id}.`);
  announce(`Saved as revision ${r.id}.`);
}
</script>

<template>
  <AppDialog :open="ui.historyOpen" labelledby="historyTitle" cls="dlg--side" @close="close">
    <div class="dlg-head">
      <h2 id="historyTitle" class="h-display">Revisions</h2>
      <button type="button" class="icon-btn" data-close aria-label="Close" @click="close"><Icon name="close" /></button>
    </div>
    <p class="dlg-lede">Each revision is frozen with its manufacturing hash. Exports belong to exactly one.</p>
    <div v-if="s.isDirty" class="rev-now">
      <span class="rev-id">Now</span>
      <div><strong>Working since {{ s.currentRevisionId || 'a new design' }}</strong><span class="muted">{{ nowChange }} · saves as {{ nextRevId() }}</span></div>
      <button type="button" class="btn btn--sm btn--ink" data-act="commit-rev" @click="commit">Save as {{ nextRevId() }}</button>
    </div>
    <p v-if="!revs.length" class="muted">No revisions yet. Confirm the critical values in Check to save the first one.</p>
    <ol class="rev-list">
      <li v-for="x in revs" :key="x.r.id" class="rev" :class="{ 'is-cur': x.cur }">
        <span class="rev-id">{{ x.r.id }}</span>
        <div class="rev-main">
          <span class="rev-title"><strong>{{ x.r.label }}</strong> <span v-if="x.cur" class="tag tag--honey">Current</span> <span v-if="x.exported" class="tag">Exported</span></span>
          <span class="muted">{{ fmtDate(x.r.createdAt) }} · <span class="mono">{{ short(x.r.manufacturingHash) }}</span></span>
          <span class="rev-changes">{{ x.changes }}</span>
        </div>
        <div class="rev-acts">
          <button type="button" class="btn btn--sm btn--quiet" :data-view-rev="x.r.id" :aria-pressed="s.viewing === x.r.id ? 'true' : undefined" @click="view(x.r.id)"><Icon name="eye" /> View<span class="sr-only"> {{ x.r.id }}</span></button>
          <button v-if="!(x.cur && !s.isDirty)" type="button" class="btn btn--sm btn--quiet" :data-restore-rev="x.r.id" @click="restore(x.r.id)">Restore<span class="sr-only"> {{ x.r.id }}</span></button>
        </div>
      </li>
    </ol>
    <p class="dlg-foot muted">View shows a revision on the model without changing anything. Restore is an edit you can undo.</p>
  </AppDialog>
</template>
